import { useAppAssetsWithSrc } from "Theme/EVENTS_ASSETS";
import { Button, EventCard, FontAwesomeIcon, Stack, Text } from "components";
import LoadingScreen from "components/LoadingScreen";
import LOCAL_STORAGE_KEYS from "constants/STORAGE_KEYS";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth, useUI } from "state";
import { useNavigationPostLogin } from "state/AuthContext";
import { useGlobal } from "state/GeneralContext";
import { ScreenPage } from "templates";
import { pushToDataLayer } from "utils/gtm";
import openToast from "utils/openToast";
import useLocalStorage from "utils/useLocalStorage";

const LOGIN_PATH = "/";

function EventListContent({ events }: { events: any[] }) {
  const { t } = useTranslation();
  const { fetchUserData } = useAuth();
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const EVENT_ASSETS = useAppAssetsWithSrc();
  const processedRef = useRef(false);
  const loginNavigator = useNavigationPostLogin();

  const onSelectEvent = async (event: any) => {
    if (!event?.id) return;

    const formCompleted = await fetchUserData(String(event.id));
    loginNavigator("", formCompleted, String(event.id));
  };

  useEffect(() => {
    if (!events?.length || processedRef.current) return;
    if (events.length === 1) {
      processedRef.current = true;
      onSelectEvent(events[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  return (
    <>
      <h2 className="font-bold mb-1 text-2xl -text--theme-colors-text-title-brand">
        {t("auth.login.eventSelectionPrompt")}
      </h2>

      <p className="-text--theme-colors-text-body text-sm">
        {t("auth.login.eventSelectionMessage")}
      </p>
      <Stack className="relative max-h-[408px]">
        <Stack className="scroll mt-4 space-y-5 max-h-[408px] overflow-auto">
          {events.map((event) => (
            <div
              key={event.id}
              role="button"
              onClick={() => {
                pushToDataLayer({
                  event: "select_event_card",
                  screen_name: "event selection",
                  journey_step: "event selection",
                });
                setSelectedEvent(event);
              }}
            >
              <EventCard
                image={event.cover_image || EVENT_ASSETS.eventPlaceholder.src}
                title={event.title}
                isSelected={event.id === selectedEvent?.id}
              />
            </div>
          ))}
        </Stack>
      </Stack>
      <Stack className="mt-8">
        <Button
          onClick={() => {
            if (!selectedEvent) {
              openToast(
                t("auth.login.eventSelectionRequiredMessage"),
                "warning",
              );
              return;
            }

            pushToDataLayer({
              event: "continue_event",
              screen_name: "event selection",
              journey_step: "event selection",
            });
            onSelectEvent(selectedEvent);
          }}
          variant="primary"
        >
          <div className="flex justify-center items-center gap-x-s10">
            <Text>{t("auth.login.continueButtonLabel")}</Text>
            <FontAwesomeIcon name="fa-chevron-right" />
          </div>
        </Button>
      </Stack>
    </>
  );
}

export default function EventSwitcherPage() {
  const [token] = useLocalStorage<string | null>(
    LOCAL_STORAGE_KEYS.token,
    null,
  );
  const [id] = useLocalStorage<string | null>(LOCAL_STORAGE_KEYS.id, null);
  const { events } = useGlobal();
  const { isAuthLoading } = useAuth();
  const { colorScheme } = useUI();
  const EVENT_ASSETS = useAppAssetsWithSrc();

  useEffect(() => {
    if (token === null && id === null) return;
    if (!token || !id) {
      globalThis.location.replace(LOGIN_PATH);
    }
  }, [token, id]);

  const showLoading =
    !token || !id || isAuthLoading || !events || events.length === 0;
  if (showLoading) {
    return <LoadingScreen />;
  }

  return (
    <ScreenPage
      desktopBG={
        colorScheme === "light"
          ? EVENT_ASSETS.login_BG_desktop.src
          : EVENT_ASSETS.login_BG_desktopDark.src
      }
      desktopBGFallback={
        colorScheme === "light"
          ? EVENT_ASSETS.login_BG_desktop.fallbackSrc
          : EVENT_ASSETS.login_BG_desktopDark.fallbackSrc
      }
      mobileBG={
        colorScheme === "light"
          ? EVENT_ASSETS.login_BG_mobile.src
          : EVENT_ASSETS.login_BG_mobileDark.src
      }
      mobileBGFallback={
        colorScheme === "light"
          ? EVENT_ASSETS.login_BG_mobile.fallbackSrc
          : EVENT_ASSETS.login_BG_mobileDark.fallbackSrc
      }
    >
      <EventListContent events={events} />
    </ScreenPage>
  );
}
